import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { AppSettings } from '../../types/settings';
import { DEFAULT_SUPPORT_EMAIL, type HintLevel } from '@padcanvas/shared';
import { InfoTip, IntegerInput } from '@shared-ui/components';

const DEFAULT_MAX_HINTS = 3;
const DEFAULT_MAX_HINT_LEVEL: HintLevel = 'guided';
const HINT_LEVEL_OPTIONS: { value: HintLevel; label: string }[] = [
  { value: 'nudge', label: 'Nudge (most Socratic)' },
  { value: 'direction', label: 'Direction' },
  { value: 'guided', label: 'Guided setup' },
  { value: 'reveal', label: 'Reveal next step (most direct)' },
];
const DEFAULT_ENRICH_BATCH_SIZE = 5;
// Mirror DEFAULT_EXTRACTION_PAGE_CHUNK_SIZE / _CHUNK_CONCURRENCY and
// MAX_EXTRACTION_PAGES in the backend's questionExtraction/extractionCore.ts.
const DEFAULT_EXTRACTION_PAGE_CHUNK_SIZE = 1;
const DEFAULT_EXTRACTION_CHUNK_CONCURRENCY = 3;
const MAX_EXTRACTION_PAGES = 20;
// Mirror DEFAULT_SOLUTION_PAGE_READ_* in the backend's
// operations/analysis/solutionPageRead/readPages.ts, and MAX_AUTO_PAGES_TENANT
// (a school's page cap — the largest upload there is).
const DEFAULT_SOLUTION_PAGE_READ_CHUNK_SIZE = 1;
const DEFAULT_SOLUTION_PAGE_READ_CONCURRENCY = 8;
const MAX_AUTO_PAGES = 40;
type PhoneOtpTransport = 'sms' | 'whatsapp';
const DEFAULT_PHONE_OTP_TRANSPORT: PhoneOtpTransport = 'sms';
const PHONE_OTP_TRANSPORT_OPTIONS: { value: PhoneOtpTransport; label: string }[] = [
  { value: 'sms', label: 'SMS (MSG91 / DLT)' },
  { value: 'whatsapp', label: 'WhatsApp (Meta authentication template)' },
];
const SOLUTION_STYLES = ['detailed', 'formal', 'quick'] as const;
type SolutionStyle = typeof SOLUTION_STYLES[number];

/**
 * App-wide defaults that students / teachers / admins inherit unless overridden.
 * Per-question overrides (e.g. Question.maxHintsForStudent) always win.
 */
export function Defaults() {
  const queryClient = useQueryClient();
  const [maxHintsForStudent, setMaxHintsForStudent] = useState<number>(DEFAULT_MAX_HINTS);
  const [maxHintLevelAllowed, setMaxHintLevelAllowed] = useState<HintLevel>(DEFAULT_MAX_HINT_LEVEL);
  const [studentUrl, setStudentUrl] = useState<string>('');
  const [teacherUrl, setTeacherUrl] = useState<string>('');
  const [adminUrl, setAdminUrl] = useState<string>('');
  const [defaultGuestSubscriptionId, setDefaultGuestSubscriptionId] = useState<string>('');
  const [renewalCodeHint, setRenewalCodeHint] = useState<string>('');
  const [enrichBatchSize, setEnrichBatchSize] = useState<number>(DEFAULT_ENRICH_BATCH_SIZE);
  const [enrichKeyCheck, setEnrichKeyCheck] = useState<boolean>(true);
  const [classifySeparately, setClassifySeparately] = useState<boolean>(false);
  const [enrichSolutionStyles, setEnrichSolutionStyles] = useState<SolutionStyle[]>(['formal']);
  const [pageChunkSize, setPageChunkSize] = useState<number>(DEFAULT_EXTRACTION_PAGE_CHUNK_SIZE);
  const [chunkConcurrency, setChunkConcurrency] = useState<number>(DEFAULT_EXTRACTION_CHUNK_CONCURRENCY);
  const [pageReadChunkSize, setPageReadChunkSize] = useState<number>(DEFAULT_SOLUTION_PAGE_READ_CHUNK_SIZE);
  const [pageReadConcurrency, setPageReadConcurrency] = useState<number>(DEFAULT_SOLUTION_PAGE_READ_CONCURRENCY);
  const [phoneOtpTransport, setPhoneOtpTransport] = useState<PhoneOtpTransport>(DEFAULT_PHONE_OTP_TRANSPORT);
  const [supportEmail, setSupportEmail] = useState<string>('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: adminApi.getSettings,
  });


  useEffect(() => {
    if (settings?.maxHintsForStudent !== undefined) {
      setMaxHintsForStudent(settings.maxHintsForStudent);
    }
    if (settings?.maxHintLevelAllowed !== undefined) {
      setMaxHintLevelAllowed(settings.maxHintLevelAllowed);
    }
    if (settings?.appUrls) {
      setStudentUrl(settings.appUrls.student ?? '');
      setTeacherUrl(settings.appUrls.teacher ?? '');
      setAdminUrl(settings.appUrls.admin ?? '');
    }
    if (settings?.defaultGuestSubscriptionId !== undefined) {
      setDefaultGuestSubscriptionId(settings.defaultGuestSubscriptionId);
    }
    if (settings?.renewalCodeHint !== undefined) {
      setRenewalCodeHint(settings.renewalCodeHint);
    }
    if (settings?.enrichQuestionsBatchSize !== undefined) {
      setEnrichBatchSize(settings.enrichQuestionsBatchSize);
    }
    if (settings?.enrichQuestionsKeyCheck !== undefined) {
      setEnrichKeyCheck(settings.enrichQuestionsKeyCheck);
    }
    if (settings?.classifyQuestionsSeparately !== undefined) {
      setClassifySeparately(settings.classifyQuestionsSeparately);
    }
    if (settings?.enrichQuestionsSolutionStyles !== undefined) {
      setEnrichSolutionStyles(settings.enrichQuestionsSolutionStyles);
    }
    if (settings?.extractionPageChunkSize !== undefined) {
      setPageChunkSize(settings.extractionPageChunkSize);
    }
    if (settings?.extractionChunkConcurrency !== undefined) {
      setChunkConcurrency(settings.extractionChunkConcurrency);
    }
    if (settings?.solutionPageReadChunkSize !== undefined) {
      setPageReadChunkSize(settings.solutionPageReadChunkSize);
    }
    if (settings?.solutionPageReadConcurrency !== undefined) {
      setPageReadConcurrency(settings.solutionPageReadConcurrency);
    }
    if (settings?.phoneOtpTransport !== undefined) {
      setPhoneOtpTransport(settings.phoneOtpTransport);
    }
    if (settings?.supportEmail !== undefined) {
      setSupportEmail(settings.supportEmail);
    }
  }, [settings]);

  // Toggle one style, keeping the array in the canonical detailed→formal→quick
  // order so the persisted value is stable regardless of click sequence.
  const toggleSolutionStyle = (style: SolutionStyle) => {
    setEnrichSolutionStyles(prev => {
      const next = prev.includes(style) ? prev.filter(s => s !== style) : [...prev, style];
      return SOLUTION_STYLES.filter(s => next.includes(s));
    });
  };

  const mutation = useMutation({
    mutationFn: (newSettings: Partial<AppSettings>) => adminApi.updateSettings(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const handleSave = () => {
    setSaveStatus('saving');
    mutation.mutate({
      maxHintsForStudent,
      maxHintLevelAllowed,
      appUrls: {
        student: studentUrl.trim(),
        teacher: teacherUrl.trim(),
        admin: adminUrl.trim(),
      },
      defaultGuestSubscriptionId: defaultGuestSubscriptionId.trim() || undefined,
      // Empty string (not undefined) so clearing it names no code — the PUT merges.
      renewalCodeHint: renewalCodeHint.trim().toUpperCase(),
      enrichQuestionsBatchSize: enrichBatchSize,
      enrichQuestionsKeyCheck: enrichKeyCheck,
      classifyQuestionsSeparately: classifySeparately,
      // Always send the array, including empty — [] deliberately means "skip
      // solutions" (keep answers + hints), distinct from unset/default.
      enrichQuestionsSolutionStyles: enrichSolutionStyles,
      // Sent only once it differs from the default or was already stored: this
      // form saves every field, and writing the default here would pin it, so a
      // later change to the backend default would never reach this environment.
      ...(pageChunkSize !== DEFAULT_EXTRACTION_PAGE_CHUNK_SIZE || settings?.extractionPageChunkSize !== undefined
        ? { extractionPageChunkSize: pageChunkSize } : {}),
      ...(chunkConcurrency !== DEFAULT_EXTRACTION_CHUNK_CONCURRENCY || settings?.extractionChunkConcurrency !== undefined
        ? { extractionChunkConcurrency: chunkConcurrency } : {}),
      ...(pageReadChunkSize !== DEFAULT_SOLUTION_PAGE_READ_CHUNK_SIZE || settings?.solutionPageReadChunkSize !== undefined
        ? { solutionPageReadChunkSize: pageReadChunkSize } : {}),
      ...(pageReadConcurrency !== DEFAULT_SOLUTION_PAGE_READ_CONCURRENCY || settings?.solutionPageReadConcurrency !== undefined
        ? { solutionPageReadConcurrency: pageReadConcurrency } : {}),
      phoneOtpTransport,
      // Empty string (not undefined) so clearing the field actually clears it —
      // the PUT merges, and an omitted key would keep the old value.
      supportEmail: supportEmail.trim(),
    }, {
      onSuccess: () => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      },
      onError: () => {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      },
    });
  };

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>App Defaults</h2>
        <p className="section-description">
          Global default values. Individual questions or other scopes can override these.
          Changes apply to all users within the backend settings cache TTL (~2 minutes).
        </p>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">Hints</h3>
        <div className="rate-limit-fields">
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Max hints for student (per question)
              <InfoTip text="Caps both pre-written hints and hints generated at runtime by the LLM. Per-question Question.maxHintsForStudent overrides this." />
            </label>
            <IntegerInput className="rate-limit-input" min={1} max={10} value={maxHintsForStudent} onChange={v => { if (v != null) setMaxHintsForStudent(v); }} />
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Max hint level
              <InfoTip text="Ceiling on how direct a runtime LLM hint can get — the level auto-escalation climbs toward. Hints still start gentle and escalate up to this cap. A teacher's per-subject Max hint level overrides this for their students." />
            </label>
            <select
              className="rate-limit-input"
              value={maxHintLevelAllowed}
              onChange={(e) => setMaxHintLevelAllowed(e.target.value as HintLevel)}
            >
              {HINT_LEVEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">App URLs</h3>
        <p className="section-description" style={{ marginTop: 0 }}>
          Public base URLs for each front-end app, used for links the backend generates
          (re-engagement deep link, invite-email fallbacks). Set these per environment —
          this dev console should hold the dev URLs. Leave blank to fall back to the prod URLs.
        </p>
        <div className="rate-limit-fields">
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Student app
              <InfoTip text="e.g. https://student.hindaun.in (dev) or https://buzzbrain.ai (prod)" />
            </label>
            <input
              type="url"
              className="rate-limit-input"
              placeholder="https://buzzbrain.ai"
              value={studentUrl}
              onChange={(e) => setStudentUrl(e.target.value)}
            />
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Teacher app
              <InfoTip text="e.g. https://teacher.hindaun.in (dev) or https://teacher.buzzbrain.ai (prod)" />
            </label>
            <input
              type="url"
              className="rate-limit-input"
              placeholder="https://teacher.buzzbrain.ai"
              value={teacherUrl}
              onChange={(e) => setTeacherUrl(e.target.value)}
            />
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Admin console
              <InfoTip text="e.g. https://admin.hindaun.in (dev) or https://admin.buzzbrain.ai (prod)" />
            </label>
            <input
              type="url"
              className="rate-limit-input"
              placeholder="https://admin.buzzbrain.ai"
              value={adminUrl}
              onChange={(e) => setAdminUrl(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">Feedback Replies</h3>
        <div className="rate-limit-fields">
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Admin email shown to users
              <InfoTip text={`Students and teachers see admin replies to their feedback as coming from this address, never the replying admin's own name or login. Admins still see who replied. Applies to past replies too. Leave blank for ${DEFAULT_SUPPORT_EMAIL}.`} />
            </label>
            <input
              type="email"
              className="rate-limit-input"
              placeholder={DEFAULT_SUPPORT_EMAIL}
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">Guests</h3>
        <div className="rate-limit-fields">
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Default guest subscription id
              <InfoTip text="Subscription assigned to a guest when the guest-subscription experiment doesn't resolve a variant. Leave blank to give guests no subscription (they hit the signup gate immediately). Must match a SubscriptionDefinition id." />
            </label>
            <input
              type="text"
              className="rate-limit-input"
              placeholder="e.g. free-ai"
              value={defaultGuestSubscriptionId}
              onChange={(e) => setDefaultGuestSubscriptionId(e.target.value)}
            />
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Renewal code shown to students
              <InfoTip text="Named on Your plan, the welcome sheet and plan-ending reminders while payments are closed. Leave empty to name no code." />
            </label>
            <input
              type="text"
              className="rate-limit-input"
              placeholder="e.g. RENEW100"
              value={renewalCodeHint}
              onChange={(e) => setRenewalCodeHint(e.target.value.toUpperCase())}
            />
          </div>
        </div>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">Phone OTP</h3>
        <p className="section-description" style={{ marginTop: 0 }}>
          Which channel carries the one-time code for phone sign-in and registration.
          A WhatsApp send that fails falls back to SMS rather than stranding someone
          mid-login, so an unapproved template surfaces as an SMS-path error, not as
          a WhatsApp one. The template itself is set under Settings &rarr; WhatsApp.
        </p>
        <div className="rate-limit-fields">
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Transport
              <InfoTip text="SMS (the default when unset) sends through MSG91 and needs MSG91_API_KEY plus a DLT template on the Function App. WhatsApp sends the Meta authentication template named by whatsapp-settings.otpTemplate, which must be approved under the Authentication category on the WABA behind whatsapp-settings.phoneNumberId." />
            </label>
            <select
              className="rate-limit-input"
              value={phoneOtpTransport}
              onChange={(e) => setPhoneOtpTransport(e.target.value as PhoneOtpTransport)}
            >
              {PHONE_OTP_TRANSPORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">Question Extraction (Assignment from PDF)</h3>
        <p className="section-description" style={{ marginTop: 0 }}>
          How a teacher's PDF/Word upload is read into questions, and the enrichment
          pass that then fills in answers, hints, and solutions.
        </p>
        <div className="rate-limit-fields">
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Pages per extraction call
              <InfoTip text={`How many PDF pages each extraction LLM call transcribes. Default ${DEFAULT_EXTRACTION_PAGE_CHUNK_SIZE}. The call also sees the following page, read-only, so a question that runs across a page break comes out whole. Raising this means fewer calls, but a dense paper can then overrun the model's output limit: a cut-off chunk is re-read in halves, which costs more than one page per call would have.`} />
            </label>
            <IntegerInput className="rate-limit-input" min={1} max={MAX_EXTRACTION_PAGES} value={pageChunkSize} onChange={v => { if (v != null) setPageChunkSize(v); }} />
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Extraction calls at once
              <InfoTip text={`How many of those page calls run in parallel. Default ${DEFAULT_EXTRACTION_CHUNK_CONCURRENCY}. Calls start in batches of this size, and each batch waits for its slowest page. Higher finishes a long paper sooner but uses more of the provider's rate limit at once, which every other feature shares.`} />
            </label>
            <IntegerInput className="rate-limit-input" min={1} max={MAX_EXTRACTION_PAGES} value={chunkConcurrency} onChange={v => { if (v != null) setChunkConcurrency(v); }} />
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Enrichment batch size
              <InfoTip text="How many extracted questions the enrich pass covers per LLM call. Default 5. Raising it cuts calls (and cost) but the model has to solve more questions in one response, which thins the later ones — so higher is cheaper but lower quality." />
            </label>
            <IntegerInput className="rate-limit-input" min={1} max={20} value={enrichBatchSize} onChange={v => { if (v != null) setEnrichBatchSize(v); }} />
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Check answer keys
              <InfoTip text="When a teacher imports a paper with an answer key, the enrichment pass works out each keyed question's answer itself (key hidden, gpt-5-mini) and flags any that differ from the key as 'Check key'. The key's answer is always kept. About $0.30–0.45 per 75-question paper. Off: keyed questions are only classified." />
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={enrichKeyCheck} onChange={e => setEnrichKeyCheck(e.target.checked)} />
              {enrichKeyCheck ? 'On' : 'Off'}
            </label>
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Classify topics separately
              <InfoTip text="File each imported question under a topic (and judge its difficulty) in a call of its own — one per subject — instead of inside the enrichment calls that solve it. The solving calls are then sent no topic list, and a solve that fails no longer costs a question its topic. Needs a Question Classification pipeline in Prompts & Pipelines; without one, imports classify in enrichment as before. Default off." />
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={classifySeparately} onChange={e => setClassifySeparately(e.target.checked)} />
              {classifySeparately ? 'On' : 'Off'}
            </label>
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Solution styles authored
              <InfoTip text="Which solutions the enrichment pass writes per question. Solutions are the expensive half of the output — the main cost dial. None = skip solutions entirely (answers + hints are still written, which is what marking and hints need). 'formal' is the benchmark answer-sheet solution. Default: formal only." />
            </label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              {SOLUTION_STYLES.map(style => (
                <label key={style} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', textTransform: 'capitalize' }}>
                  <input
                    type="checkbox"
                    checked={enrichSolutionStyles.includes(style)}
                    onChange={() => toggleSolutionStyle(style)}
                  />
                  {style}
                </label>
              ))}
              {enrichSolutionStyles.length === 0 && (
                <span className="section-description" style={{ margin: 0 }}>
                  None — solutions skipped (answers + hints still authored)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rate-limit-tier">
        <h3 className="rate-limit-tier-title">Uploaded Solutions (Page Reader)</h3>
        <p className="section-description" style={{ marginTop: 0 }}>
          How the page reader reads a student's uploaded solution pages, beside the
          splitter. It runs only for students with the solution_page_reader gate on.
        </p>
        <div className="rate-limit-fields">
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Pages per page-reader call
              <InfoTip text={`How many uploaded pages each page-reader LLM call reads. Default ${DEFAULT_SOLUTION_PAGE_READ_CHUNK_SIZE}; at ${MAX_AUTO_PAGES} every upload is read in one call. Cost should barely change with this, since most of a call's cost is its output and the output depends on what is on the pages. More pages per call risks hitting the model's output limit on a dense upload, and the slowest call finishes later.`} />
            </label>
            <IntegerInput className="rate-limit-input" min={1} max={MAX_AUTO_PAGES} value={pageReadChunkSize} onChange={v => { if (v != null) setPageReadChunkSize(v); }} />
          </div>
          <div className="rate-limit-field">
            <label className="rate-limit-label">
              Page-reader calls at once
              <InfoTip text={`How many of those calls run in parallel. Default ${DEFAULT_SOLUTION_PAGE_READ_CONCURRENCY}. They run beside the splitter, and the student waits for whichever finishes last, so this should be high enough that a long upload is read within the time the split takes. Higher uses more of the provider's rate limit at once, which every other feature shares.`} />
            </label>
            <IntegerInput className="rate-limit-input" min={1} max={MAX_AUTO_PAGES} value={pageReadConcurrency} onChange={v => { if (v != null) setPageReadConcurrency(v); }} />
          </div>
        </div>
      </div>

      <div className="rate-limit-actions">
        <button
          className="rate-limit-save-btn"
          onClick={handleSave}
          disabled={mutation.isPending}
        >
          {mutation.isPending && saveStatus === 'saving' ? 'Saving...' : 'Save Defaults'}
        </button>
        {saveStatus === 'saved' && <span className="status-saved">Defaults saved!</span>}
        {saveStatus === 'error' && <span className="status-error">Failed to save</span>}
      </div>
    </div>
  );
}
